import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import GlobalStyles from '../components/ui/GlobalStyles';
import LoginPage from '../components/auth/LoginPage';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import { useAuth, useAuthActions } from '../context/AuthContext';
import { startKeycloakLogin } from '../auth/oidc';
import { markInviteWelcome } from '../utils/inviteWelcome';
import { ROUTE_PATTERNS } from './routes';

// BAR-40 S2: auth guard route element. Logged out → render the login overlay
// (NOT a <Navigate to="/login">) so the deep-link URL stays in the address bar
// and the SAME url re-renders authed after login — matching the app's current
// overlay-on-top-of-the-URL behavior. Logged in → <Outlet/> (the shell). Lifts
// the logged-out branch that used to live in App's render.
export default function RequireAuth() {
  const {
    isAuthenticated, resetToken, inviteToken, signupToken, connectPending,
    authMode, connectReturnPath, loginNotice, loginNoticeTone, forceLoginPrompt,
    sessionRestorePending, signingOut,
  } = useAuth();
  const { handleLoginSuccess, setResetToken, setInviteToken, setSignupToken, handleSignOutLate } = useAuthActions();
  const navigate = useNavigate();

  // BAR-1564: what keeps the app's own page up in keycloak mode apart from a
  // link dialog: the startup restore still running (or unknown), a sign-out
  // under way, a connector sign-up needing the consent shown here, a message
  // to show.
  const keycloakPageHeld = sessionRestorePending !== false || Boolean(signingOut) || Boolean(connectPending) || Boolean(loginNotice);
  const autoRedirect = authMode === 'keycloak' && !keycloakPageHeld && !inviteToken && !signupToken && !resetToken;

  // BAR-1564: what LoginPage is still waiting on before it may show its
  // sign-in or go to Keycloak — a sign-out under way, else the startup
  // restore. Only in keycloak mode, and only while no link dialog is open
  // (the dialog needs the page). connectPending and loginNotice hold the page
  // by themselves; they are not something to wait for.
  const waitingFor = authMode === 'keycloak' && !inviteToken && !signupToken && !resetToken
    ? (signingOut ? 'signOut' as const : sessionRestorePending !== false ? 'restore' as const : null)
    : null;

  // BAR-1564: in keycloak mode a password set from a reset or invite link ends
  // on Keycloak's sign-in page; the modal closes first, so the sign-in page is
  // what stays if the redirect cannot start. With nothing else holding the
  // page, clearing the token lets LoginPage's own redirect go (once); starting
  // a second one here would race it. `replace`, as LoginPage does, so Back
  // does not return to a page that only forwards again. The credentials
  // prompt applies here too: after a refusal or a failed sign-out, Keycloak
  // may still hold the old session.
  const continueToKeycloak = (
    clearToken: (value: null) => void,
    otherDialogLeft: boolean,
    beforeContinue?: () => void,
  ) => (
    authMode === 'keycloak'
      ? () => {
          beforeContinue?.();
          clearToken(null);
          if (!keycloakPageHeld && !otherDialogLeft) return;
          startKeycloakLogin({
            returnTo: connectReturnPath ?? '/',
            forceLogin: Boolean(forceLoginPrompt),
            replace: true,
          }).catch((error: unknown) => {
            console.error('[Auth] Could not start the Keycloak sign-in:', error);
          });
        }
      : undefined
  );

  if (!isAuthenticated) {
    return (
      <>
        <GlobalStyles />
        {/* While a connector sign-in waits (someone sent here by Claude or
            ChatGPT), the page also offers a free account through Google or
            Microsoft, so a new person need not register somewhere else first
            and lose the waiting connection on the way. */}
        {/* BAR-1564: in keycloak mode the page sends people to Keycloak and
            back; a waiting connector hand-off is what they return to. */}
        <LoginPage
          onLoginSuccess={handleLoginSuccess}
          offerSignup={Boolean(connectPending)}
          authMode={authMode ?? 'firebase'}
          returnTo={connectReturnPath ?? undefined}
          notice={loginNotice ?? null}
          noticeTone={loginNoticeTone ?? 'error'}
          waitingFor={waitingFor}
          forceLoginPrompt={Boolean(forceLoginPrompt)}
          onSignOutLate={handleSignOutLate}
          // BAR-1564: in keycloak mode the page may send the browser to
          // Keycloak by itself — but only when nothing here needs it (the
          // restore, a sign-out, a connector consent, a notice, a dialog).
          autoRedirect={autoRedirect}
        />
        {/* Invite takes precedence over reset if both query params are present
            (a freshly-invited user shouldn't see "reset" copy). A free-signup
            activation (BAR-1528) sits between them: it also creates the
            account, so it outranks a reset. */}
        {inviteToken ? (
          <ForgotPasswordModal
            key={`invite:${inviteToken}`}
            initialToken={inviteToken}
            mode="invite"
            onClose={() => setInviteToken(null)}
            onPasswordSet={continueToKeycloak(
              setInviteToken,
              Boolean(signupToken || resetToken),
              markInviteWelcome,
            )}
          />
        ) : signupToken ? (
          <ForgotPasswordModal
            key={`signup:${signupToken}`}
            initialToken={signupToken}
            mode="signup"
            onClose={() => setSignupToken?.(null)}
            // A new free account works in an AI assistant, not here: sign it
            // in the way /login does and open the page that connects one.
            // `replace`, so Back does not return to the spent activation URL.
            // BAR-1564: a Keycloak session only comes from Keycloak, so in
            // keycloak mode the modal shows its "activated" view instead.
            onLoginSuccess={authMode === 'keycloak' ? undefined : (token, user, refreshToken) => {
              handleLoginSuccess(token, user, refreshToken);
              navigate(ROUTE_PATTERNS.connect, { replace: true });
            }}
          />
        ) : resetToken ? (
          <ForgotPasswordModal
            key={`reset:${resetToken}`}
            initialToken={resetToken}
            mode="reset"
            onClose={() => setResetToken(null)}
            onPasswordSet={continueToKeycloak(setResetToken, false)}
          />
        ) : null}
      </>
    );
  }

  return <Outlet />;
}
